import {
  Table as TableInterface,
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  createColumnHelper,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
  flexRender,
  ColumnDef,
  Column,
} from '@tanstack/react-table';
import InfiniteScroll from 'react-infinite-scroll-component';
import React from 'react';

import { TableHeader, TableHead, TableBody, TableCell, TableRow, Table } from '../table/table';
import { cn } from '../../utils/class-merge';
import { Loading } from '../loading/loading';

interface DataTableProps<T> {
  noResultsComponent?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  table: TableInterface<T>;
  columnsLength: number;
  className?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  fetchNextPage: () => void;
  hasMore: boolean;
}

const InfiniteScrollDataTable = <T,>({
  noResultsComponent,
  isFetching = false,
  isLoading = false,
  loadingComponent,
  fetchNextPage,
  columnsLength,
  className,
  hasMore,
  table,
}: DataTableProps<T>) => {
  return (
    <div
      className={cn(
        'rounded-bl-lg rounded-br-lg overflow-y-hidden overflow-x-auto [&>div]:max-h-full [&>div]:h-full',
        className,
      )}
    >
      <InfiniteScroll
        loader={<Loading isLoading={isFetching} className="p-4" element="div" size="small" />}
        next={() => {
          fetchNextPage();
        }}
        dataLength={table.getRowModel().rows.length}
        className="max-h-full"
        hasMore={hasMore}
        height="100%"
      >
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    style={{
                      minWidth: header.column.columnDef.size,
                      maxWidth: header.column.columnDef.maxSize,
                    }}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={columnsLength}>
                  {loadingComponent || <Loading className="p-0" element="div" size="small" />}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow data-state={row.getIsSelected() && 'selected'} key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      style={{
                        minWidth: cell.column.columnDef.size,
                        maxWidth: cell.column.columnDef.maxSize,
                      }}
                      className="truncate"
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              !isFetching && (
                <TableRow>
                  <TableCell className="h-24 text-center" colSpan={columnsLength}>
                    {noResultsComponent || '-'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </InfiniteScroll>
    </div>
  );
};

export {
  type ColumnFiltersState,
  InfiniteScrollDataTable,
  getPaginationRowModel,
  type PaginationState,
  type TableInterface,
  getFilteredRowModel,
  createColumnHelper,
  type SortingState,
  getSortedRowModel,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
  type Column,
};
